import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Field from 'legacy/common/components/wysiwyg-form/Field';
import Header from 'legacy/common/components/wysiwyg-form/Header';
import { useCreateComplimentaryTickets } from '../../../../../hooks';

type ComplimentaryTicketFormProps = {
  onCancel: () => void;
  eventId: string;
  refetchTickets: () => void;
  isDraft?: boolean;
  isEditing?: boolean;
  isLoading?: boolean;
  lastUpdated?: string;
};

const ComplimentaryTicketForm = ({
  onCancel,
  eventId,
  refetchTickets,
  isDraft = false,
  isEditing = false,
  isLoading = false,
  lastUpdated
}: ComplimentaryTicketFormProps) => {
  const { t } = useTranslation(['events']);
  const { mutate: createTickets } = useCreateComplimentaryTickets();

  const ComplimentaryTicketsSchema = Yup.object().shape({
    name: Yup.string()
      .max(100)
      .required(t('events:pricingTiers.complimentaryTicketForm.validation.required'))
      .label(t('events:pricingTiers.complimentaryTicketForm.validation.nameLabel')),
    quantity: Yup.number()
      .min(0)
      .max(999999)
      .required(t('events:pricingTiers.complimentaryTicketForm.validation.required'))
      .label(t('events:pricingTiers.complimentaryTicketForm.validation.availableTicketsLabel'))
  });

  const onSubmit = ({ name, quantity }) => {
    createTickets(
      { eventId, name, quantity },
      {
        onSuccess: () => {
          refetchTickets();
          onCancel();
        },
        onError: () => {
          onCancel();
        }
      }
    );
  };

  return (
    <>
      <Header
        isDraft={isDraft}
        isEditing={isEditing}
        title={t('events:pricingTiers.complimentaryTicketForm.title')}
        isLoading={isLoading}
        lastUpdated={lastUpdated}
        onBack={onCancel}
      />
      <div className="pt-[50px] md:pt-[180px] -mt-[90px] pb-20 md:w-[630px] md:mx-auto">
        <div className="p-4">
          <Formik
            initialValues={{ name: '', quantity: '' }}
            onSubmit={onSubmit}
            validationSchema={ComplimentaryTicketsSchema}
          >
            {({ isValid, submitForm }) => (
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <Form>
                  <div className="mb-6">
                    <Field
                      id="name"
                      name="name"
                      type="text"
                      label={t('events:pricingTiers.complimentaryTicketForm.nameLabel')}
                      placeholder={t('events:pricingTiers.complimentaryTicketForm.namePlaceholder')}
                    />
                    <Field
                      id="quantity"
                      name="quantity"
                      type="number"
                      label={t('events:pricingTiers.complimentaryTicketForm.availableTicketsLabel')}
                      placeholder={t('events:pricingTiers.complimentaryTicketForm.availableTicketsPlaceholder')}
                    />
                  </div>
                </Form>

                <div className="flex justify-center w-full fixed left-0 bottom-0 bg-white pb-8 z-50 md:pb-4 border-t-1 px-4 py-4 shadow-primary-50 shadow-top md:shadow-none">
                  <Button
                    className="w-full md:max-w-[343px] bg-[#3853C4]! text-white"
                    type={ButtonType.Primary}
                    disabled={!isValid}
                    testId="button-confirm-ticket"
                    onClick={submitForm}
                  >
                    {t('events:pricingTiers.complimentaryTicketForm.createTicketsButton')}
                  </Button>
                </div>
              </div>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default ComplimentaryTicketForm;
